import React, { useEffect, useState } from 'react'
import View from '../atoms/View'
import { useDispatch, useSelector } from 'react-redux'
import { RootReducer } from 'src/redux/rootReducer'
import { useTranslation } from 'react-i18next'
import { Form, Field } from 'react-final-form'
import Space from '../atoms/Space'
import Text from '../atoms/TextNew'
import Input from '../atoms/Input'
import Button from '../atoms/Button'
import { UPDATE_PROFILE_PASSWORD } from 'dealer-reducers'
import useConstant from 'src/helpers/useConstants'
import { createForm } from 'final-form'

interface FormProps {
  currentPassword: string
  newPassword: string
  passwordConfirmation: string
}

const SettingsUpdatePassword = (): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const updatePasswordFail = useSelector((state: RootReducer) => state.userDataReducer.updatePasswordError)
  const updatePasswordSuccess = useSelector((state: RootReducer) => state.userDataReducer.updatePasswordSuccess)

  const isLoading = useSelector((state: RootReducer) => state.loaderReducer.isLoading)
  const [updatePasswordError, setUpdatePasswordError] = useState('')

  useEffect(() => {
    if (updatePasswordFail) {
      setUpdatePasswordError(t('update_password_form.current_password_invalid'))
      return
    }
    setUpdatePasswordError('')
  }, [updatePasswordFail, updatePasswordSuccess, t])

  const onSubmit = ({ currentPassword, newPassword, passwordConfirmation }: FormProps): void => {
    if (newPassword !== passwordConfirmation) {
      setUpdatePasswordError(t('update_password_form.password_not_match'))
      return
    }
    if (newPassword.length < 8) {
      setUpdatePasswordError(t('update_password_form.password_to_short'))
      return
    }
    const regex = /^(?=.*[!@#$%^&*])(?=.*[A-Z])(?=.*[a-z]).+$/
    // Contains at least 1 special character
    // Contains at least 1 uppercase letter
    // Contains at least 1 lowercase letter
    if (!regex.test(newPassword)) {
      setUpdatePasswordError(t('update_password_form.password_do_not_meet_requirements'))
      return
    }

    dispatch(
      UPDATE_PROFILE_PASSWORD({
        currentPassword,
        newPassword,
        passwordConfirmation
      })
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression, @typescript-eslint/no-explicit-any
  const form: any = useConstant(() =>
    createForm({
      onSubmit
    })
  )

  useEffect(() => {
    form.reset()
    setUpdatePasswordError('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatePasswordSuccess])

  return (
    <View className="mx-10">
      <Form
        form={form}
        onSubmit={onSubmit}
        render={({ handleSubmit }) => {
          return (
            <form>
              <Space y={8} />
              <View
                style={{
                  height: window.innerHeight - 400
                }}
                className="flex flex-col justify-between">
                <View className="grid grid-cols-2 gap-10">
                  <View className="mt-2">
                    <Text className="ml-4 text-xl font-SemiBold">{t('set_password').toUpperCase()}</Text>
                    <Space y={2} />
                    <View className="grid grid-cols-2 bg-white pt-6 rounded-3">
                      <View className="col-span-2 mr-8 ml-2">
                        <Field name="currentPassword">
                          {({ input }) => (
                            <Input
                              onChange={input.onChange}
                              label={t('update_password_form.current_password')}
                              type="password"
                              value={input.value}
                              containerStyles={{ borderRadius: '12px', border: '1px solid #bebebe' }}
                            />
                          )}
                        </Field>
                        <Space y={4} />
                        <Field name="newPassword">
                          {({ input }) => (
                            <Input
                              onChange={input.onChange}
                              label={t('update_password_form.new_password')}
                              type="password"
                              value={input.value}
                              containerStyles={{ borderRadius: '12px', border: '1px solid #bebebe' }}
                            />
                          )}
                        </Field>
                        <Space y={4} />
                        <Field name="passwordConfirmation">
                          {({ input }) => (
                            <Input
                              onChange={input.onChange}
                              label={t('update_password_form.confirm_new_password')}
                              type="password"
                              value={input.value}
                              containerStyles={{ borderRadius: '12px', border: '1px solid #bebebe' }}
                            />
                          )}
                        </Field>
                        {}
                        <Space y={2} />
                        {updatePasswordError.length > 0 && (
                          <Text className="text-errorRed text-l text-center">{updatePasswordError}</Text>
                        )}
                        {updatePasswordSuccess && updatePasswordError.length === 0 && (
                          <Text className="text-successGreen text-l text-center">
                            {t('update_password_form.new_password')}
                          </Text>
                        )}
                        <Space y={8} />
                      </View>
                    </View>
                  </View>
                  <Space y={10} />
                </View>

                <View className="max-w-[200px] mx-auto mb-10">
                  <Button
                    isLoading={isLoading}
                    customStyles="w-[150px]"
                    color="groupBlue"
                    onClick={() => {
                      handleSubmit()?.catch(null)
                    }}>
                    <Text className="text-xl text-white font-SemiBold text-center">
                      <> {t('profile_settings_form.save')}</>
                    </Text>
                  </Button>
                </View>
              </View>
              <Space y={6} />
            </form>
          )
        }}
      />
    </View>
  )
}

export default SettingsUpdatePassword
